import { Repeat, Sparkles, User, Zap } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { cn } from '@/lib/utils';
import type { AssignmentMethod } from '@/types/lead';

const METHOD_CONFIG: Record<
  AssignmentMethod,
  { label: string; icon: typeof Zap; className: string }
> = {
  AUTO: {
    label: 'Auto',
    icon: Zap,
    className:
      'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700/50 dark:bg-blue-950/30 dark:text-blue-300',
  },
  MANUAL: {
    label: 'Manual',
    icon: User,
    className: 'border-border bg-secondary text-secondary-foreground',
  },
  ROUND_ROBIN: {
    label: 'Round robin',
    icon: Repeat,
    className:
      'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-300',
  },
  AI: {
    label: 'AI',
    icon: Sparkles,
    className:
      'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-700/50 dark:bg-violet-950/30 dark:text-violet-300',
  },
};

/**
 * Compact badge showing HOW a lead was assigned (its `assignedBy` method).
 * Returns null for an unset method so callers can render it unconditionally.
 */
export function AssignmentMethodBadge({
  method,
  className,
}: {
  method: AssignmentMethod | null | undefined;
  className?: string;
}) {
  if (!method) return null;
  const config = METHOD_CONFIG[method];
  if (!config) return null;
  const Icon = config.icon;
  return (
    <Badge
      variant="outline"
      className={cn('h-5 gap-1 px-1.5 text-[10px] font-medium', config.className, className)}
    >
      <Icon className="size-3" aria-hidden />
      {config.label}
    </Badge>
  );
}
